import { components } from "@/api-schema";
import {
  PathsV1ApplicationSearchPostParametersQueryExtend,
  PathsV1AppRoleAttributeSearchPostParametersQueryExtend,
  PathsV1GroupUuidGetParametersQueryExtend,
} from "@/api-schema-enums";
import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useApplicationSearchQuery } from "@/common/api/application/use-application-search.query";
import { useAttributeSearchQuery } from "@/common/api/attribute/use-attribute-search.query";
import { useGroupByUuidQuery } from "@/common/api/group/use-group-by-uuid.query";
import { useGroupUpdateMutation } from "@/common/api/group/use-group-update.mutation";
import { filterAppRolesWithoutAttributes } from "@/common/helpers/filter-app-roles-without-attributes";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Form } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "wouter";
import { createGroupDetailPath, GroupDetailTab } from "../_helpers/group-detail-path";
import { formSchema, formSchemaType, GroupAppRoleFormFields } from "./_components/app-role-form-fields";

type Application = components["schemas"]["Application"];
type AppRoleAttribute = components["schemas"]["AppRoleAttribute"];

export function AppRoleView() {
  const { t } = useTranslation(["ram", "common"]);
  const [_, navigate] = useLocation();
  const params = useParams<{ groupUuid: string }>();
  const { isPending, mutateAsync } = useGroupUpdateMutation(params.groupUuid);
  const [searchValue, setSearchValue] = useState<string>("");
  const [pagination, setPagination] = useState({ page: 1, pageSize: DEFAULT_PAGE_SIZE });
  const form = useForm<formSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      appRoles: [],
    },
  });

  const applicationSearchQueryInstance = useApplicationSearchQuery({
    options: {
      parameters: {
        extend: [
          PathsV1ApplicationSearchPostParametersQueryExtend.AppRoles,
          PathsV1ApplicationSearchPostParametersQueryExtend.AppRoles_AppRoleIdentifiers,
        ],
        search: searchValue || undefined,
        page: pagination.page,
        pageSize: pagination.pageSize,
      },
    },
  });
  const groupByUuidQueryInstance = useGroupByUuidQuery(params.groupUuid, {
    parameters: {
      extend: [PathsV1GroupUuidGetParametersQueryExtend.AppRoles],
    },
  });
  const attributesQueryInstance = useAttributeSearchQuery({
    options: {
      parameters: {
        pageSize: -1,
        extend: [PathsV1AppRoleAttributeSearchPostParametersQueryExtend.Application],
      },
    },
  });

  const attributesByUuid = useMemo(() => {
    const attributes: AppRoleAttribute[] = attributesQueryInstance.data?.data || [];
    return new Map<string, AppRoleAttribute>(attributes.map((attr) => [attr.uuid, attr]));
  }, [attributesQueryInstance.data?.data]);

  const applicationsWithoutAttributeRoles = useMemo(
    () =>
      applicationSearchQueryInstance.data?.data?.map((application: Application) => ({
        ...application,
        appRoles: filterAppRolesWithoutAttributes(application.appRoles),
      })) ?? [],
    [applicationSearchQueryInstance.data?.data],
  );

  const { setValue } = form;

  useEffect(() => {
    setValue("appRoles", groupByUuidQueryInstance.data?.appRoles?.map((item) => item.uuid) || []);
  }, [groupByUuidQueryInstance.data, setValue]);

  const onSubmit = async (values: formSchemaType) => {
    await toastMutationStatus({
      mutationFn: () => mutateAsync({ ...values, appRoles: values.appRoles || [] }),
      successMessage: t("ram:AppRolesHaveBeenUpdated"),
      errorMessage: t("ram:ErrorInUpdatingppRoles"),
    });

    navigate(createGroupDetailPath(params.groupUuid, GroupDetailTab.AppRoles));
  };

  return (
    <CenteredLayout
      variant={CenteredLayoutVariant.Table}
      title={groupByUuidQueryInstance.data?.name ?? t("ram:AppRoles")}
      subTitle={t("ram:HereYouCanSelectTheAppRolesForThisGroup")}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col gap-6">
          <GroupAppRoleFormFields
            data={applicationsWithoutAttributeRoles}
            isLoading={
              isPending ||
              applicationSearchQueryInstance.isLoading ||
              groupByUuidQueryInstance.isLoading ||
              attributesQueryInstance.isLoading
            }
            form={form}
            groupUuid={params.groupUuid}
            attributesByUuid={attributesByUuid}
            onSearchChange={setSearchValue}
            page={pagination.page}
            pageSize={pagination.pageSize}
            totalItems={applicationSearchQueryInstance.data?.meta?.total}
            onPaginationChange={setPagination}
          />
        </form>
      </Form>
    </CenteredLayout>
  );
}
